// @ts-nocheck
import { hydrateChoiceMetadata } from './trainingChoiceMetadata.ts';
import {
  canonicalizeTrainingSlug,
  TRAINING_SLUGS as PUBLIC_TRAINING_SLUGS,
} from './trainingSlugs.ts';
import { buildVideoTranscript } from './videoTranscripts.ts';
import { CURRICULUM_VERSION } from './trainingConstants.ts';
import { flattenStudioChapters } from './studioCurriculumUtils.ts';
import { ensureTrainingChapterVisuals } from './trainingVisualSlides.ts';
import { tx, TRAINING_VIDEOS, TRAINING_TOOLKIT, DEFAULT_PREFLIGHT } from './mvpCurriculumContent.ts';
import { buildMvpCurriculum } from './mvpCurriculumBuilder.ts';

export const TRAINING_SLUG = PUBLIC_TRAINING_SLUGS.mvp;
export { CURRICULUM_VERSION, flattenStudioChapters };
export { TRAINING_VIDEOS };

export const TRAINING_SLUGS = Object.values(PUBLIC_TRAINING_SLUGS);

const STUDIO_CURRICULA = {
  [TRAINING_SLUG]: buildMvpCurriculum(TRAINING_SLUG, CURRICULUM_VERSION),
};
const loadedSpecialistTraining = new Map();

const TRAINING_TOOLKITS = {
  [TRAINING_SLUG]: TRAINING_TOOLKIT,
};

const TRAINING_PREFLIGHTS = {
  [TRAINING_SLUG]: DEFAULT_PREFLIGHT,
};

function localize(value, language) {
  const lang = String(language || 'en').toLowerCase().startsWith('fr') ? 'fr' : 'en';
  if (Array.isArray(value)) return value.map((item) => localize(item, lang));
  if (!value || typeof value !== 'object') return value;
  const keys = Object.keys(value);
  if (keys.length === 2 && keys.includes('en') && keys.includes('fr')) return value[lang];
  return Object.fromEntries(keys.map((key) => [key, localize(value[key], lang)]));
}

export function hasStudioTraining(slug) {
  return TRAINING_SLUGS.includes(canonicalizeTrainingSlug(slug));
}

export function getStudioCurriculum(language = 'en', slug = TRAINING_SLUG) {
  const canonicalSlug = canonicalizeTrainingSlug(slug);
  const curriculum = STUDIO_CURRICULA[canonicalSlug] || loadedSpecialistTraining.get(canonicalSlug)?.curriculum;
  if (!curriculum) return null;
  return ensureTrainingChapterVisuals(hydrateChoiceMetadata(localize(curriculum, language), language));
}

export function getStudioVideo(videoId, language = 'en', curriculumOrSlug = TRAINING_SLUG) {
  const slug = canonicalizeTrainingSlug(typeof curriculumOrSlug === 'string'
    ? curriculumOrSlug
    : curriculumOrSlug?.slug || TRAINING_SLUG);
  const curriculum = typeof curriculumOrSlug === 'object'
    ? curriculumOrSlug
    : STUDIO_CURRICULA[slug] || loadedSpecialistTraining.get(slug)?.curriculum;
  const override = curriculum?.videoOverrides?.[videoId] || {};
  const video = TRAINING_VIDEOS[videoId] ? { ...TRAINING_VIDEOS[videoId], ...override } : null;
  if (!video) return null;
  const localizedVideo = localize(video, language);
  const localizedCurriculum = localize(curriculum, language);
  return {
    id: videoId,
    captions: [
      { language: 'en', src: '' },
      { language: 'fr', src: '' },
    ],
    transcript: buildVideoTranscript({
      curriculum: localizedCurriculum,
      videoId,
      videoTitle: localizedVideo.title,
      language,
    }),
    chapters: localize([
      { seconds: 0, label: tx('Context and goal', 'Contexte et objectif') },
      { seconds: 60, label: tx('Guided demonstration', 'Démonstration guidée') },
      { seconds: 180, label: tx('What to save', 'Ce qu’il faut enregistrer') },
    ], language),
    ...localizedVideo,
  };
}

export function getStudioToolkit(language = 'en', slug = TRAINING_SLUG) {
  const canonicalSlug = canonicalizeTrainingSlug(slug);
  return localize(TRAINING_TOOLKITS[canonicalSlug] || loadedSpecialistTraining.get(canonicalSlug)?.toolkit || TRAINING_TOOLKIT, language);
}

export function getStudioPreflight(language = 'en', slug = TRAINING_SLUG) {
  const canonicalSlug = canonicalizeTrainingSlug(slug);
  return localize(TRAINING_PREFLIGHTS[canonicalSlug] || loadedSpecialistTraining.get(canonicalSlug)?.preflight || DEFAULT_PREFLIGHT, language);
}

export async function loadStudioTraining(language = 'en', slug = TRAINING_SLUG) {
  const canonicalSlug = canonicalizeTrainingSlug(slug);
  if (canonicalSlug === TRAINING_SLUG) {
    return {
      curriculum: getStudioCurriculum(language, canonicalSlug),
      toolkit: getStudioToolkit(language, canonicalSlug),
      preflight: getStudioPreflight(language, canonicalSlug),
    };
  }

  const { loadSpecialistCurriculum } = await import('./specialistCurricula.ts');
  const specialist = await loadSpecialistCurriculum(canonicalSlug);
  if (!specialist) return { curriculum: null, toolkit: [], preflight: [] };
  loadedSpecialistTraining.set(canonicalSlug, specialist);
  return {
    curriculum: ensureTrainingChapterVisuals(hydrateChoiceMetadata(localize(specialist.curriculum, language), language)),
    toolkit: localize(specialist.toolkit || [], language),
    preflight: localize(specialist.preflight || DEFAULT_PREFLIGHT, language),
  };
}

export function findStudioChapter(curriculum, dayId, chapterId) {
  const day = curriculum.days.find((item) => item.id === dayId) || null;
  const chapterItem = day?.chapters.find((item) => item.id === chapterId) || null;
  return { day, chapter: chapterItem };
}
